import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { LEGAL_ROUTES } from '@admin-platform/shared/constants';
import { useConsent } from './ConsentContext';
import type { ConsentPreferences } from './consentStore';
import './CookieBanner.css';

export function CookieBanner() {
  const { bannerVisible, dismissible, preferences, savePreferences, closePreferences } =
    useConsent();
  const [expanded, setExpanded] = useState(false);
  const [draft, setDraft] = useState<ConsentPreferences>(preferences);

  // Reseed the manage toggles from the current preferences each time the
  // banner is (re)opened.
  useEffect(() => {
    if (bannerVisible) setDraft(preferences);
  }, [bannerVisible, preferences]);

  if (!bannerVisible) return null;

  return (
    <div
      className="cookie-banner"
      role="dialog"
      aria-modal="false"
      aria-label="Cookie consent"
    >
      <div className="cookie-banner__inner">
        {dismissible && (
          <button
            type="button"
            className="cookie-banner__close"
            aria-label="Close cookie settings"
            onClick={closePreferences}
          >
            ×
          </button>
        )}

        <div className="cookie-banner__body">
          <h2 className="cookie-banner__title">We value your privacy</h2>
          <p className="cookie-banner__text">
            We use strictly necessary cookies to run Revvly. With your consent we also use analytics
            and marketing cookies to understand usage and measure our campaigns. See our{' '}
            <Link to={LEGAL_ROUTES.cookies}>Cookie Policy</Link> for details.
          </p>

          {expanded && (
            <fieldset className="cookie-banner__options">
              <legend className="sr-only">Cookie preferences</legend>

              <label className="cookie-banner__option cookie-banner__option--locked">
                <input type="checkbox" checked disabled />
                <span>
                  <strong>Strictly necessary</strong>, always on. Required to sign in and secure
                  the service.
                </span>
              </label>

              <label className="cookie-banner__option">
                <input
                  type="checkbox"
                  checked={draft.analytics}
                  onChange={(e) => setDraft((d) => ({ ...d, analytics: e.target.checked }))}
                />
                <span>
                  <strong>Analytics</strong>, helps us understand how the site is used.
                </span>
              </label>

              <label className="cookie-banner__option">
                <input
                  type="checkbox"
                  checked={draft.marketing}
                  onChange={(e) => setDraft((d) => ({ ...d, marketing: e.target.checked }))}
                />
                <span>
                  <strong>Marketing</strong>, measures the performance of our advertising.
                </span>
              </label>
            </fieldset>
          )}
        </div>

        <div className="cookie-banner__actions">
          {expanded ? (
            <button
              type="button"
              className="cookie-banner__btn cookie-banner__btn--accent"
              onClick={() => savePreferences(draft)}
            >
              Save preferences
            </button>
          ) : (
            <>
              <button
                type="button"
                className="cookie-banner__btn cookie-banner__btn--ghost"
                onClick={() => savePreferences({ analytics: false, marketing: false })}
              >
                Reject non-essential
              </button>
              <button
                type="button"
                className="cookie-banner__btn cookie-banner__btn--ghost"
                onClick={() => setExpanded(true)}
              >
                Manage
              </button>
              <button
                type="button"
                className="cookie-banner__btn cookie-banner__btn--accent"
                onClick={() => savePreferences({ analytics: true, marketing: true })}
              >
                Accept all
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
